<template>
  <div class="capital-page">
    <div class="panel-heading">
      <h4 class="panel-title">{{ $t('finance.capitalTitle') }}</h4>
    </div>

    <div
      class="panel-body capital-page__panel-body"
      style="display: block;"
    >
      <v-layout
        row
        wrap
        class="mb-3 capital-page__toolbar"
      >
        <v-flex
          xs12
          class="px-2"
        >
          <v-btn
            color="primary"
            style="padding: 0 16px !important;"
            @click="loadDashboard"
          >
            {{ $t('finance.refresh') }}
          </v-btn>
          <v-btn
            v-if="!apiData.isInitialized && $_has_perm('add_money')"
            color="warning"
            class="ml-2"
            style="padding: 0 16px !important;"
            @click="initModal = true"
          >
            {{ $t('finance.initializeCapital') }}
          </v-btn>
          <v-btn
            v-if="apiData.isInitialized && $_has_perm('add_money')"
            color="info"
            class="ml-2"
            style="padding: 0 16px !important;"
            @click="movementModal = true"
          >
            {{ $t('finance.depositWithdraw') }}
          </v-btn>
        </v-flex>
      </v-layout>

      <v-card class="mb-4">
        <v-card-title>
          <span>{{ $t('finance.bookSummary', { from: apiData.periodFrom, to: apiData.periodTo }) }}</span>
        </v-card-title>
        <v-card-text>
          <table class="table table-bordered">
            <tbody>
              <tr>
                <th class="grey lighten-3">{{ $t('finance.initializedCapital') }}</th>
                <td>
                  {{ apiData.initialCapital | usdFormat }}
                  <br >
                  <small v-if="apiData.initialCapitalSetAt">{{ apiData.initialCapitalSetAt }}</small>
                </td>
                <th class="grey lighten-3">{{ $t('finance.totalDeposits') }}</th>
                <td>{{ apiData.totalDeposits | usdFormat }}</td>
                <th class="grey lighten-3">{{ $t('finance.totalWithdraws') }}</th>
                <td>{{ apiData.totalWithdraws | usdFormat }}</td>
              </tr>
              <tr>
                <th />
                <td />
                <th class="grey lighten-3">{{ $t('finance.revenueIncome') }}</th>
                <td>{{ apiData.revenue | usdFormat }}</td>
                <th class="grey lighten-3">{{ $t('finance.expensesTitle') }}</th>
                <td>{{ apiData.expensesTotal | usdFormat }}</td>
              </tr>
              <tr>
                <th
                  colspan="6"
                  class="text-center"
                >
                  <h5 class="success--text">
                    {{ $t('finance.netCapitalBook', { amount: $options.filters.usdFormat(apiData.netCapitalBook) }) }}
                  </h5>
                </th>
              </tr>
            </tbody>
          </table>
        </v-card-text>
      </v-card>

      <h5
        v-if="$_has_perm('view_money')"
        class="mb-2"
      >
        {{ $t('finance.capitalMovements') }}
      </h5>
      <div
        v-if="$_has_perm('view_money')"
        class="row"
      >
        <preDatatableComponent
          :permissions="{ add_money: 'add_money' }"
          :removeAddButton="true"
          :removeWarehouseDeliverButton="true"
          :tableData="tableData"
          :resetGetResults="resetGetResults"
          :resetGetResults2="resetGetResults2"
          :searchMe="searchMe"
          :pagination="pagination"
          :perPageFix="perPageFix"
          :filterTable="filterTable"
          :addMoneyButton="apiData.isInitialized === true && $_has_perm('add_money')"
          :addMoneyButtonLabel="$t('finance.depositWithdraw')"
          :openAddMoneyModal="openMovementModal"
          :columns="columns"
          :showHideColumn="showHideColumn"
          :filterMade="filterMade"
          :totalResult="totalResult"
          @branch-edit="branchEdit"
        />
        <div class="col-xs-12">
          <div class="row text-center">
            <span
              id="test-editable-capital"
              style="opacity:0"
            />
          </div>
          <datatable
            :sortDir="tableData.sortDir"
            :columns="columns"
            :sortByColumn="sortByColumn"
            :searchOneCol="searchOneCol"
            :sortKey="sortKey"
            :sortOrders="sortOrders"
          >
            <tbody>
              <tr
                v-for="r in apiResults"
                :key="r.put_money_id"
              >
                <td>{{ r.bank_name }}</td>
                <td>{{ formatMovementType(r.type) }}</td>
                <td>{{ r.amount }}</td>
                <td>{{ r.full_name }}</td>
                <td>{{ r.note }}</td>
                <td>{{ r.created_at }}</td>
                <td v-if="$_has_perm('delete_money')">
                  <a
                    :tooltip="$t('common.delete')"
                    @click="deleteMovement(r.put_money_id)"
                  >
                    <v-icon color="red darken-2">delete</v-icon>
                  </a>
                </td>
              </tr>
              <tr id="no-result-row-capital">
                <td
                  :colspan="columns.length"
                  class="text-center"
                >
                  <v-alert
                    type="error"
                    :value="alertValue"
                  >
                    {{ $t('common.noResultsFound') }}
                  </v-alert>
                </td>
              </tr>
            </tbody>
          </datatable>

          <hr >
          <div class="row">
            <div class="col-xs-12 text-right">
              <paginate
                :page-count="pagination.lastPage"
                :page-range="2"
                :changePage="changePage"
                :container-class="'pagination'"
                :pagination="pagination"
                :page-class="'page-item'"
              />
            </div>
          </div>
        </div>
      </div>

      <capital-initialize-modal
        v-model="initModal"
        @saved="onInitialSaved"
      />
      <capital-movement-modal
        v-model="movementModal"
        @saved="onMovementSaved"
      />
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import swal from 'sweetalert'
import PaginationVue from '../Pagination.vue'
import preDatatableComponent from '../preDatatableComponent.vue'
import DatatableVue from '../Datatable.vue'
import CapitalInitializeModal from './modals/CapitalInitializeModal.vue'
import CapitalMovementModal from './modals/CapitalMovementModal.vue'

export default {
  name: 'CapitalPage',

  components: {
    preDatatableComponent,
    datatable: DatatableVue,
    paginate: PaginationVue,
    CapitalInitializeModal,
    CapitalMovementModal
  },

  data() {
    const sortOrders = {}
    const columns = [
      {
        label: this.$t('columns.book'),
        text: this.$t('columns.book'),
        value: '1',
        sortable: true,
        searchable: true,
        name: 'bank_name'
      },
      {
        label: this.$t('columns.type'),
        text: this.$t('columns.type'),
        value: '2',
        sortable: true,
        searchable: true,
        name: 'type'
      },
      {
        label: this.$t('columns.amount'),
        text: this.$t('columns.amount'),
        value: '3',
        sortable: true,
        searchable: true,
        name: 'amount'
      },
      {
        label: this.$t('columns.staff'),
        text: this.$t('columns.staff'),
        value: '4',
        sortable: true,
        searchable: true,
        name: 'full_name'
      },
      {
        label: this.$t('columns.note'),
        text: this.$t('columns.note'),
        value: '5',
        sortable: true,
        searchable: true,
        name: 'note'
      },
      {
        label: this.$t('common.date'),
        text: this.$t('common.date'),
        value: '6',
        sortable: true,
        searchable: true,
        name: 'created_at'
      }
    ]
    columns.forEach((column) => {
      sortOrders[column.name] = -1
    })

    return {
      apiData: {
        initialCapital: 0,
        initialCapitalSetAt: null,
        isInitialized: false,
        totalDeposits: 0,
        totalWithdraws: 0,
        revenue: 0,
        expensesTotal: 0,
        netCapitalBook: 0,
        periodFrom: '',
        periodTo: ''
      },
      initModal: false,
      movementModal: false,
      url: 'api/capital_movements',
      alertValue: false,
      filterMade: false,
      totalResult: 0,
      apiResults: [],
      columns,
      sortKey: 'put_money_id',
      sortOrders,
      prevSearchQuery: '',
      tableData: {
        per_page: 10,
        search: '',
        column: 'created_at',
        sortDir: 'desc',
        searchOneCol: '',
        searchOneVal: '',
        from_date: '',
        to_date: '',
        branches_id: ''
      },
      pagination: {
        lastPage: 1,
        currentPage: 1,
        total: 1,
        lastPageUrl: '',
        nextPageUrl: '',
        prevPageUrl: '',
        from: '',
        to: ''
      }
    }
  },

  mounted() {
    this.loadDashboard().then(() => {
      if (this.$_has_perm('view_money')) {
        this.perPageFix(this.tableData.per_page)
      }
    })
  },

  methods: {
    formatMovementType(t) {
      if (t === 'withdraw') return this.$t('finance.withdraw')
      return this.$t('finance.deposit')
    },

    openMovementModal() {
      this.movementModal = true
    },

    onInitialSaved() {
      this.loadDashboard()
    },

    onMovementSaved() {
      this.loadDashboard()
      this.getResults()
    },

    loadDashboard() {
      const token = localStorage.getItem('access_token')
      return axios
        .get('api/capital', { headers: { Authorization: 'Bearer ' + token } })
        .then((res) => {
          const d = res.data
          this.apiData = {
            initialCapital: d.initialCapital,
            initialCapitalSetAt: d.initialCapitalSetAt,
            isInitialized: d.isInitialized,
            totalDeposits: d.totalDeposits,
            totalWithdraws: d.totalWithdraws,
            revenue: d.revenue,
            expensesTotal: d.expensesTotal,
            netCapitalBook: d.netCapitalBook,
            periodFrom: d.periodFrom,
            periodTo: d.periodTo
          }
        })
        .catch(() => {})
    },

    branchEdit(branches) {
      this.tableData.branches_id = branches.toString()
      this.getResults()
    },

    showHideColumn(prevColVal, currentColVal) {
      const a = []
      const diff = []

      if (currentColVal.length < prevColVal.length) {
        for (let i = 0; i < prevColVal.length; i++) {
          a[prevColVal[i]] = true
        }

        for (let i = 0; i < currentColVal.length; i++) {
          if (a[currentColVal[i]]) {
            delete a[currentColVal[i]]
          } else {
            a[currentColVal[i]] = true
          }
        }

        for (const k in a) {
          diff.push(k)
        }

        for (let c = 0; c < diff.length; c++) {
          $('td:nth-child(' + diff[c] + '),th:nth-child(' + diff[c] + ')').toggle()
        }
      } else {
        for (let i = 0; i < currentColVal.length; i++) {
          const visible = $('td:nth-child(' + currentColVal[i] + '),th:nth-child(' + currentColVal[i] + ')').is(
            ':visible'
          )
          if (visible) {
            $('td:nth-child(' + currentColVal[i] + '),th:nth-child(' + currentColVal[i] + ')').toggle()
          }
        }
      }
    },

    deleteMovement(id) {
      swal({
        title: this.$t('common.areYouSure'),
        icon: 'warning',
        buttons: true
      }).then((willDelete) => {
        if (willDelete) {
          const token = localStorage.getItem('access_token')
          axios
            .delete(`api/capital_movements/${id}`, { headers: { Authorization: 'Bearer ' + token } })
            .then(() => {
              this.getResults()
              this.loadDashboard()
              swal({ title: this.$t('common.done'), text: '', icon: 'success', timer: 1500, buttons: false })
            })
            .catch(() => {})
        }
      })
    },

    getResults(url = this.url) {
      const token = localStorage.getItem('access_token')
      const theDatatable = document.querySelector('.the-datatable')
      const thePanel = document.querySelector('.panel-flat')

      if (theDatatable != null) {
        theDatatable.classList.add('slide-table-out')
        if (thePanel) thePanel.style.overflowX = 'auto'
      }

      axios
        .get(url, { params: this.tableData, headers: { Authorization: 'Bearer ' + token } })
        .then((response) => {
          const data = response.data
          const isFlatArray = Array.isArray(data.data)
          if (isFlatArray) {
            this.filterMade = true
            this.apiResults = data.data
          } else {
            this.filterMade = false
            this.apiResults = data.data.data || []
          }

          if (this.apiResults.length === 0) {
            this.tableData.searchOneCol = ''
            this.tableData.searchOneVal = ''
            this.tableData.search = ''
            this.alertValue = true
          } else {
            this.alertValue = false
          }
          if (isFlatArray) {
            this.removeConfigPagination()
          } else {
            this.configPagination(data.data)
          }
          if (theDatatable) theDatatable.classList.remove('slide-table-out')
          setTimeout(() => {
            if (thePanel) thePanel.style.overflowX = 'auto'
          }, 0)
        })
        .then(() => {
          this.totalResult = this.apiResults.length
        })
        .catch(() => {})
    },

    removeConfigPagination() {
      this.pagination.lastPage = ''
      this.pagination.currentPage = ''
      this.pagination.total = ''
      this.pagination.lastPageUrl = ''
      this.pagination.nextPageUrl = ''
      this.pagination.prevPageUrl = ''
      this.pagination.from = ''
      this.pagination.to = ''
    },

    resetGetResults() {
      this.tableData.searchOneCol = ''
      this.tableData.searchOneVal = ''
      this.tableData.search = ''
      this.sortKey = 'created_at'
      this.tableData.sortDir = 'desc'
      this.tableData.column = 'created_at'
      this.tableData.from_date = ''
      this.tableData.to_date = ''
      this.getResults()
    },

    resetGetResults2() {
      this.resetGetResults()
    },

    perPageFix(perPage) {
      this.tableData.per_page = perPage
      this.getResults()
    },

    changePage(pageUrl) {
      this.url = pageUrl
      this.getResults()
    },

    searchMe(str) {
      this.tableData.searchOneCol = ''
      this.tableData.searchOneVal = ''
      if (str.trim() !== '') {
        this.tableData.search = str
        this.prevSearchQuery = str
        this.getResults()
      } else {
        swal({
          text: this.$t('common.typeToSearch'),
          icon: 'warning',
          dangerMode: true
        })
        if (this.prevSearchQuery !== '') {
          this.tableData.search = ''
          this.prevSearchQuery = ''
          this.getResults()
        }
      }
    },

    sortByColumn(str) {
      this.tableData.column = str
      this.tableData.sortKey = str
      this.tableData.sortDir = this.tableData.sortDir === 'desc' ? 'asc' : 'desc'
      this.getResults()
    },

    searchOneCol(columnName, str) {
      this.tableData.searchOneCol = columnName
      this.tableData.searchOneVal = str
      this.getResults()
    },

    filterTable(fromDate, toDate) {
      this.tableData.from_date = fromDate
      this.tableData.to_date = toDate
      this.getResults()
    },

    configPagination(data) {
      this.pagination.lastPage = data.last_page
      this.pagination.currentPage = data.current_page
      this.pagination.total = data.total
      this.pagination.lastPageUrl = data.last_page_url
      this.pagination.nextPageUrl = data.next_page_url
      this.pagination.prevPageUrl = data.prev_page_url
      this.pagination.from = data.from
      this.pagination.to = data.to
    }
  }
}
</script>

<style scoped>
.capital-page {
  overflow: visible;
}

.panel-heading h4 {
  text-transform: uppercase;
  margin-left: -20px;
  padding-left: 10px;
  font-weight: 500;
}

/* Same idea as allOrder change-status dialog: avoid clipping v-btn bottoms (overflow/shadow). */
.capital-page__panel-body {
  overflow: visible;
}

.capital-page__toolbar {
  overflow: visible;
  padding-top: 12px;
  padding-bottom: 12px;
}

.capital-page .table th {
  vertical-align: middle;
}
</style>



// WEBPACK FOOTER //
// src/components/capital/CapitalPage.vue